import { Box, CircularProgress, ClickAwayListener, Icon, IconButton, InputAdornment, TableCell, TableRow } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "../../../../localization/useTranslation";
import { DotLegalLink, DotLegalTable, DotLegalTextField, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import AiChatIcon from "@common/svgs/aiChatIcon.svg?react";
import AiFeatureGate from "../../../../plan/aiFeatureGate/AiFeatureGate";
import { canDescribeDocument } from "../TaskDocumentation.hooks";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { DocumentUpdateModel } from "../../../../common/documents/documentModel";
import { useDotLegalFileUploadDataMapping } from "../../../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import DotLegalDeleteDialogWrapper from "../../../../common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper";
import { isNullOrWhitespace } from "../../../../common/stringOperations";
import { EntityType } from "../../../../common/Common.types";
import { TaskDocumentationViewModel } from "../TaskDocumentation.types";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../../useUrlProvider";

export interface ITaskDocumentationTable {
    documents: Array<TaskDocumentationViewModel> | undefined;
    onRemoveDocument: (documentId: string) => void;
    onRemovePolicy: (id: string) => void;
    disabled?: boolean;
    onEditDocument: (document: DocumentUpdateModel) => void;
    selectedDocument: TaskDocumentationViewModel | undefined;
    setSelectedDocument: (model: TaskDocumentationViewModel | undefined) => void;
    entityType: EntityType;
    entityId: string;
    onDocumentClick?: (document: TaskDocumentationViewModel) => void;
    onDescribeDocument?: (documentId: string) => void;
    describingDocumentId?: string;
    showAiDescribe?: boolean;
}

// The AI icon is a hardcoded-gradient SVG, so MUI's disabled coloring cannot dim it. Match the locked look manually.
const lockedIconOpacity = 0.38;
const aiIconSize = { "& svg": { height: 18, width: 15 } };

interface IAiDescribeButtonProps {
    locked: boolean;
    isTooLargeForAi: boolean;
    isDescribing: boolean;
    onClick: () => void;
}

function AiDescribeButton(props: IAiDescribeButtonProps) {
    const { translateString } = useTranslation();

    // When locked the click must bubble to the surrounding PlanDialogWrapper, so this is a disabled *look*,
    // not a disabled button (which would swallow the event). The wrapper supplies the upsell tooltip.
    if (props.locked) {
        return (
            <IconButton size="small" sx={{ opacity: lockedIconOpacity, ...aiIconSize }}>
                <AiChatIcon />
            </IconButton>
        );
    }

    // A disabled button fires no hover events, so the span carries the tooltip.
    if (props.isTooLargeForAi) {
        return (
            <DotLegalTooltip text={translateString("textExtractionErrorFileTooLarge")}>
                <Box component={"span"}>
                    <IconButton size="small" disabled sx={{ opacity: lockedIconOpacity, ...aiIconSize }}>
                        <AiChatIcon />
                    </IconButton>
                </Box>
            </DotLegalTooltip>
        );
    }

    return (
        <DotLegalTooltip text={props.isDescribing ? translateString("aiGenerating") : translateString("aiDescribeDocumentation")}>
            <IconButton
                size="small"
                disabled={props.isDescribing}
                sx={aiIconSize}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick();
                }}
            >
                {props.isDescribing ? <CircularProgress size={16} /> : <AiChatIcon />}
            </IconButton>
        </DotLegalTooltip>
    );
}

function TaskDocumentationTable(props: ITaskDocumentationTable) {
    const { translateString, translateDate } = useTranslation();
    const { downloadDocument } = useDotLegalFileUploadDataMapping();
    const navigate = useNavigate();
    const { getPolicyUrl } = useUrlProvider();

    const textFieldRef = React.useRef<HTMLTextAreaElement>();
    useEffect(() => {
        if (props.selectedDocument) {
            textFieldRef.current?.focus();
            let endIndex = isNullOrWhitespace(props.selectedDocument.dataId) ? textFieldRef.current?.value.length : textFieldRef.current?.value.indexOf(".");
            textFieldRef.current?.setSelectionRange(0, endIndex!);
        }
    }, [props.selectedDocument]);

    const getHeaders = () => {
        let headers: Array<ITableHeader<TaskDocumentationViewModel>> = [];
        headers.push({ property: "name", text: translateString("documentation"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("date"), align: "left", showOnMobile: true });
        headers.push({ property: null, text: "" });

        return headers;
    };

    const handleRowClick = (row: TaskDocumentationViewModel) => {
        if (textFieldRef.current) {
            return;
        }

        if (row.isPolicy) {
            navigate(getPolicyUrl(row.id));
            return;
        }

        if (props.onDocumentClick) {
            props.onDocumentClick(row);
            return;
        }

        const isFileDocument = !isNullOrWhitespace(row.dataId);
        if (isFileDocument) {
            downloadDocument(row.id, row.name!, props.entityType, props.entityId);
        } else {
            window.open(row.link, "_blank");
        }
    };

    return (
        <Box>
            <DotLegalTable
                headers={getHeaders()}
                emptyText={translateString("noDocuments")}
                paginationLabelOf={translateString("labelOf")}
                defaultOrderBy={"created"}
                defaultOrder={"desc"}
                isLoading={false}
                hidePagination
                textStyle={"NoCapsHeaders"}
                data={props.documents!}
                marginTop={1}
                clickableRows={props.selectedDocument === undefined}
                rowsPerPage={1000}
                renderRow={(row) => (
                    <TableRow
                        sx={{
                            "&:hover": {
                                button: {
                                    visibility: "visible",
                                },
                            },
                        }}
                        key={row.id}
                        onClick={() => handleRowClick(row)}
                    >
                        {props.selectedDocument?.id === row.id ? (
                            <ClickAwayListener onClickAway={() => props.setSelectedDocument(undefined)}>
                                <TableCell>
                                    <Box sx={{ mt: "5px" }}>
                                        <DotLegalTextField
                                            noMargin
                                            variant="standard"
                                            label={""}
                                            value={row.name ?? row.link!}
                                            debounce
                                            inputRef={textFieldRef}
                                            onChange={(value) => {
                                                if (isNullOrWhitespace(row.dataId)) {
                                                    props.onEditDocument({ id: row.id, fileName: undefined, link: value });
                                                } else {
                                                    props.onEditDocument({ id: row.id, fileName: value, link: undefined });
                                                }
                                            }}
                                            onEnterPressed={() => props.setSelectedDocument(undefined)}
                                            endAdorment={
                                                props.showAiDescribe && canDescribeDocument(row) ? (
                                                    <InputAdornment position="end">
                                                        <AiFeatureGate>
                                                            {({ locked }) => (
                                                                <AiDescribeButton
                                                                    locked={locked}
                                                                    isTooLargeForAi={row.isTooLargeForAi}
                                                                    isDescribing={props.describingDocumentId === row.id}
                                                                    onClick={() => props.onDescribeDocument?.(row.id)}
                                                                />
                                                            )}
                                                        </AiFeatureGate>
                                                    </InputAdornment>
                                                ) : undefined
                                            }
                                        />
                                    </Box>
                                </TableCell>
                            </ClickAwayListener>
                        ) : (
                            <TableCell>
                                {row.isPolicy ? (
                                    <DotLegalLink bold linkColor={"primary"} to={getPolicyUrl(row.id)} stopPropagation>
                                        {row.name}
                                    </DotLegalLink>
                                ) : (
                                    (row.name ?? row.link!)
                                )}
                            </TableCell>
                        )}

                        <TableCell sx={{ whiteSpace: "nowrap", width: 100 }}>{translateDate(row.created)}</TableCell>

                        <TableCell width={40}>
                            {props.disabled === false && (
                                <Box sx={{ display: "flex" }}>
                                    <IconButton
                                        sx={{ visibility: "hidden", opacity: row.isPolicy ? 0 : 1, pointerEvents: row.isPolicy ? "none" : "auto" }}
                                        size="small"
                                        onClick={(e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
                                            if (props.selectedDocument) {
                                                props.setSelectedDocument(undefined);
                                            } else {
                                                props.setSelectedDocument(row);
                                            }
                                            e.stopPropagation();
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>

                                    <IconButton
                                        sx={{ visibility: "hidden" }}
                                        size="small"
                                        onClick={(e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
                                            e.stopPropagation();
                                            if (row.isPolicy) {
                                                props.onRemovePolicy(row.id);
                                            } else {
                                                props.onRemoveDocument(row.id);
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                    </IconButton>
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                )}
            />
        </Box>
    );
}

export default TaskDocumentationTable;
